import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaArrowLeft,
  FaBuilding,
  FaLock,
  FaLockOpen,
  FaPlus,
  FaUser,
  FaUserShield,
  FaUserTag,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { useUsers } from "../hooks/useUsers";
import Header from "../components/adminUsers/Header";
import SearchBar from "../components/adminUsers/SearchBar";
import UserCard from "../components/adminUsers/UserCard";
import EmptyState from "../components/adminUsers/EmptyState";
import UserForm from "../components/adminUsers/UserForm";
import { Helmet } from "react-helmet-async";

export default function AdminUsers() {
  const navigate = useNavigate();
  const [isAdding, setIsAdding] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    password: "",
    roles: ["Restaurant"],
  });
  // eslint-disable-next-line no-unused-vars
  const [formErrors, setFormErrors] = useState({});
  const usersContainerRef = useRef(null);
  const firstUserRef = useRef(null);
  const isPaginationChange = useRef(false);

  const {
    filteredUsers,
    isLoading,
    isAdmin,
    assigningRole,
    setAssigningRole,
    checkAdminAndFetchUsers,
    searchUsers,
    handleAssignRole,
    handleToggleStatus,
    handleSubmitUser,
    getSortedUsers,
    getAvailableRolesToAssign,
    getFilteredAvailableRoles,
    isCurrentUser,
    currentPage,
    totalPages,
    searchTerm,
    setSearchTerm,
    isSearching,
    handlePageChange,
    handlePrevPage,
    handleNextPage,
    getPaginationNumbers,
  } = useUsers();

  const scrollToFirstUser = () => {
    if (isPaginationChange.current) {
      setTimeout(() => {
        if (firstUserRef.current) {
          const offset = 120;
          const elementPosition =
            firstUserRef.current.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - offset;

          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
          });
        } else if (usersContainerRef.current) {
          const containerPosition =
            usersContainerRef.current.getBoundingClientRect().top;
          const offsetPosition = containerPosition + window.pageYOffset - 20;

          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
          });
        }
      }, 200);

      isPaginationChange.current = false;
    }
  };

  useEffect(() => {
    if (!isLoading && filteredUsers.length > 0) {
      scrollToFirstUser();
    }
  }, [filteredUsers, isLoading, currentPage]);

  const handlePageChangeWithScroll = (pageNum) => {
    if (pageNum !== currentPage) {
      isPaginationChange.current = true;
      handlePageChange(pageNum);
    }
  };

  const handlePrevPageWithScroll = () => {
    if (currentPage > 1) {
      isPaginationChange.current = true;
      handlePrevPage();
    }
  };

  const handleNextPageWithScroll = () => {
    if (currentPage < totalPages) {
      isPaginationChange.current = true;
      handleNextPage();
    }
  };

  const filteredAvailableRoles = getFilteredAvailableRoles();

  const showWarningAlert = (title, message) => {
    if (window.innerWidth < 768) {
      toast.warning(message || title, {
        position: "top-right",
        autoClose: 2500,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          width: "70%",
          margin: "10px",
          borderRadius: "8px",
          textAlign: "right",
          fontSize: "14px",
          direction: "rtl",
        },
      });
    } else {
      Swal.fire({
        icon: "warning",
        title: title || "تحذير",
        text: message,
        confirmButtonColor: "#2E3D88",
        background: "#ffffff",
        color: "#000000",
        showConfirmButton: false,
        timer: 2500,
      });
    }
  };

  useEffect(() => {
    const initialize = async () => {
      const hasAccess = await checkAdminAndFetchUsers();
      if (!hasAccess) {
        navigate("/");
      }
    };
    initialize();
  }, [checkAdminAndFetchUsers, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormErrors({});

    if (!isFormValid()) {
      showWarningAlert("نموذج غير مكتمل", "يرجى ملء جميع الحقول المطلوبة.");
      return;
    }

    const userData = {
      ...formData,
      roles: Array.isArray(formData.roles) ? formData.roles : [formData.roles],
    };

    const result = await handleSubmitUser(userData, resetForm);
    if (result.errors) {
      setFormErrors(result.errors);
    }
  };

  const resetForm = () => {
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      password: "",
      roles: ["Restaurant"],
    });
    setFormErrors({});
    setIsAdding(false);
  };

  useEffect(() => {
    if (searchTerm.trim() === "") {
      checkAdminAndFetchUsers();
    }
  }, [searchTerm, checkAdminAndFetchUsers]);

  const handleAddNewUser = () => {
    setIsAdding(true);
    setFormErrors({});

    if (window.innerWidth < 1280) {
      setTimeout(() => {
        document.getElementById("user-form")?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "Admin":
        return "bg-red-100 text-red-800 border-red-200";
      case "Restaurant":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "Branch":
        return "bg-green-100 text-green-800 border-green-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case "Admin":
        return <FaUserShield className="text-xs sm:text-sm" />;
      case "Restaurant":
        return <FaBuilding className="text-xs sm:text-sm" />;
      case "Branch":
        return <FaUserTag className="text-xs sm:text-sm" />;
      default:
        return <FaUser className="text-xs sm:text-sm" />;
    }
  };

  const getStatusBadge = (user) => {
    if (user.isActive === false) {
      return (
        <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-semibold border border-red-200 flex items-center gap-1">
          <FaLock className="text-xs" />
          معطل
        </span>
      );
    }
    return (
      <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-semibold border border-green-200 flex items-center gap-1">
        <FaLockOpen className="text-xs" />
        مفعل
      </span>
    );
  };

  const isFormValid = () => {
    const basicFieldsValid =
      formData.firstName.trim() !== "" &&
      formData.lastName.trim() !== "" &&
      formData.email.trim() !== "" &&
      formData.roles.length === 1;

    if (!isAdding) {
      return basicFieldsValid && formData.password.trim() !== "";
    }

    return basicFieldsValid;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white via-[#f0f3ff] to-[#d9e0f5] px-4">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-[#2E3D88]"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  const sortedUsers = getSortedUsers(filteredUsers);

  return (
    <>
      <Helmet>
        <title>Triple-S | تريبل اس</title>
        <meta
          name="description"
          content="تريبل اس شركة برمجيات تقدم تطوير مواقع وتطبيقات وأنظمة إدارة حديثة، بحلول رقمية احترافية تعزز حضورك الرقمي وتدعم نمو أعمالك."
        />
      </Helmet>
      <div
        className={`min-h-screen bg-gradient-to-br from-white via-[#f0f3ff] to-[#d9e0f5] px-3 sm:px-4 md:px-6 py-3 sm:py-6 relative font-sans overflow-hidden transition-colors duration-300`}
        style={{ direction: "rtl" }}
      >
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -left-10 sm:-left-20 -top-10 sm:-top-20 w-40 h-40 sm:w-60 sm:h-60 md:w-80 md:h-80 bg-gradient-to-r from-[#2E3D88]/10 to-[#4A5DB0]/10 rounded-full blur-2xl sm:blur-3xl animate-pulse"></div>
          <div className="absolute -right-10 sm:-right-20 -bottom-10 sm:-bottom-20 w-40 h-40 sm:w-60 sm:h-60 md:w-80 md:h-80 bg-gradient-to-r from-[#4A5DB0]/10 to-[#2E3D88]/10 rounded-full blur-2xl sm:blur-3xl animate-pulse"></div>
        </div>

        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate(-1)}
          className="fixed top-3 sm:top-4 left-3 sm:left-4 z-50 bg-white/80 backdrop-blur-md hover:bg-[#2E3D88] hover:text-white rounded-full p-2 sm:p-3 text-[#2E3D88] border border-[#2E3D88]/30 shadow-lg transition-all duration-300 hover:scale-110 hover:shadow-xl group"
        >
          <FaArrowLeft
            size={14}
            className="sm:size-4 group-hover:scale-110 transition-transform"
          />
        </motion.button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, type: "spring" }}
          className="max-w-7xl mx-auto bg-white/90 backdrop-blur-xl shadow-xl sm:shadow-2xl rounded-2xl sm:rounded-3xl border border-white/50 relative overflow-hidden"
        >
          <Header />

          <div className="relative px-3 sm:px-4 md:px-6 lg:px-8 pb-4 sm:pb-6 md:pb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex justify-center -mt-6 sm:-mt-7 md:-mt-8 mb-6 sm:mb-8 md:mb-10"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleAddNewUser}
                className="flex items-center gap-2 bg-gradient-to-r from-[#2E3D88] to-[#4A5DB0] text-white px-4 sm:px-5 md:px-6 py-3 sm:py-3 md:py-4 rounded-xl sm:rounded-2xl font-semibold shadow-2xl sm:shadow-3xl hover:shadow-4xl hover:shadow-[#2E3D88]/50 transition-all duration-300 text-sm sm:text-base md:text-lg border-2 border-white whitespace-nowrap transform translate-y-2"
              >
                <FaPlus className="text-sm sm:text-base md:text-lg" />
                <span>إضافة مستخدم جديد</span>
              </motion.button>
            </motion.div>

            <SearchBar
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              searchUsers={searchUsers}
              isSearching={isSearching}
            />

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-5 md:gap-6">
              <div
                ref={usersContainerRef}
                className={`space-y-3 sm:space-y-4 md:space-y-5 ${
                  isAdding ? "xl:col-span-2" : "xl:col-span-3"
                }`}
              >
                {sortedUsers.length > 0 ? (
                  <>
                    {sortedUsers.map((user, index) => (
                      <UserCard
                        key={user.id}
                        ref={index === 0 ? firstUserRef : null}
                        user={user}
                        index={index}
                        isCurrentUser={isCurrentUser}
                        getRoleBadgeColor={getRoleBadgeColor}
                        getRoleIcon={getRoleIcon}
                        getStatusBadge={getStatusBadge}
                        getAvailableRolesToAssign={getAvailableRolesToAssign}
                        assigningRole={assigningRole}
                        setAssigningRole={setAssigningRole}
                        handleAssignRole={handleAssignRole}
                        handleToggleStatus={handleToggleStatus}
                      />
                    ))}

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                      <div className="mt-6 sm:mt-8 flex flex-col items-center">
                        <div className="flex items-center justify-center gap-1 sm:gap-2">
                          <button
                            onClick={handlePrevPageWithScroll}
                            disabled={currentPage === 1}
                            className={`p-2 sm:p-3 rounded-xl ${
                              currentPage === 1
                                ? "bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed"
                                : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600"
                            }`}
                          >
                            <FaChevronRight className="text-sm sm:text-base" />
                          </button>

                          <div className="flex items-center gap-1 sm:gap-2">
                            {getPaginationNumbers().map((pageNum, index) => (
                              <React.Fragment key={index}>
                                {pageNum === "..." ? (
                                  <span className="px-2 sm:px-3 py-1 sm:py-2 text-gray-500">
                                    ...
                                  </span>
                                ) : (
                                  <button
                                    onClick={() =>
                                      handlePageChangeWithScroll(pageNum)
                                    }
                                    className={`px-3 sm:px-4 py-1 sm:py-2 rounded-xl font-semibold ${
                                      currentPage === pageNum
                                        ? "bg-gradient-to-r from-[#2E3D88] to-[#4A5DB0] text-white shadow-lg"
                                        : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600"
                                    }`}
                                  >
                                    {pageNum}
                                  </button>
                                )}
                              </React.Fragment>
                            ))}
                          </div>

                          <button
                            onClick={handleNextPageWithScroll}
                            disabled={currentPage === totalPages}
                            className={`p-2 sm:p-3 rounded-xl ${
                              currentPage === totalPages
                                ? "bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed"
                                : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600"
                            }`}
                          >
                            <FaChevronLeft className="text-sm sm:text-base" />
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <EmptyState
                    searchTerm={searchTerm}
                    handleAddNewUser={handleAddNewUser}
                  />
                )}
              </div>

              <AnimatePresence>
                <UserForm
                  isAdding={isAdding}
                  formData={formData}
                  setFormData={setFormData}
                  availableRoles={filteredAvailableRoles}
                  handleSubmit={handleSubmit}
                  resetForm={resetForm}
                  getRoleIcon={getRoleIcon}
                  isFormValid={isFormValid}
                />
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
}
